<template>
  <div
    v-if="filterInternal != null"
    :data-filter-type="filterInternal.type"
    class="table-filter">
    <div
      v-if="isExpanded"
      ref="filterBg"
      class="table-filter-bg"
      @click="isExpanded = false" />
    <!-- NUMERIC FILTER -->
    <div v-if="filterInternal.type === 'NumericFilter'">
      <div
        class="filter numeric"
        @click="toggleNumericDropDown()">
        <div class="label">
          {{ filterInternal.label }}
          <span
            v-if="filterInternal.comparisonOption != null && filterInternal.firstValue != null"
            class="value">
            {{ filterInternal.comparisonOption.label }} {{ filterInternal.firstValue }}
            <span v-if="filterInternal.hasSecondValue">– {{ filterInternal.secondValue }}</span>
          </span>
          <span
            v-if="
              filterInternal.comparisonOption != null &&
              filterInternal.comparisonOption.id === NumericFilterComparisonOptions.IsEmpty.id
            "
            >: {{ $t('messages.empty') }}</span
          >
        </div>
        <div class="expand">
          <icon-arrow-down />
        </div>
        <div
          class="delete"
          @click.stop.prevent="remove">
          <icon-circle-x />
        </div>
      </div>
      <div
        ref="expandedPane"
        class="expanded-pane"
        :class="{ 'anchor-right': shouldAnchorRight, 'anchor-bottom': shouldAnchorBottom, visible: isExpanded }">
        <div class="inputs">
          <select @change="(e) => onNumericFilterComparisonOptionChanged(e)">
            <option
              v-for="(option, i) in availableNumericalFilterComparisonOptions"
              :key="i"
              :selected="numericFilterComparisonOption != null ? numericFilterComparisonOption.id === option.id : null"
              :value="option.id">
              {{ option.label }}
            </option>
          </select>
          <template
            v-if="
              numericFilterComparisonOption != null &&
              numericFilterComparisonOption.id !== NumericFilterComparisonOptions.IsEmpty.id
            ">
            <div>
              <input
                v-model="numericFilterFirstValue"
                type="text"
                class="form-control table-text-filter"
                @change="onNumericFilterFirstValueChange"
                @keyup.enter="commitNumericFilterChanges" />
              <span
                v-if="wasCommitted && !isFirstValueValid"
                class="error"
                >{{ $t('messages.invalidInput') }}</span
              >
            </div>
            <div v-if="numericFilterComparisonOption.id === NumericFilterComparisonOptions.Between.id">
              <input
                v-model="numericFilterSecondValue"
                type="text"
                class="form-control table-text-filter"
                @change="onNumericFilterSecondValueChange"
                @keyup.enter="commitNumericFilterChanges" />
              <span
                v-if="wasCommitted && !isSecondValueValid"
                class="error"
                >{{ $t('messages.invalidInput') }}</span
              >
            </div>
          </template>
        </div>
        <div class="actions">
          <button
            class="btn btn-sm btn-primary-hollow"
            :disabled="!isValid"
            @click="commitNumericFilterChanges">
            {{ capitalize($t('actions.update')) }}
          </button>
          <button
            class="btn btn-sm btn-default"
            @click="isExpanded = !isExpanded">
            {{ capitalize($t('actions.cancel')) }}
          </button>
        </div>
      </div>
    </div>
    <!-- DATE FILTER -->
    <div v-else-if="filterInternal.type === 'DateFilter'">
      <div
        class="filter numeric"
        @click="toggleDateDropDown()">
        <div class="label">
          {{ filterInternal.label
          }}<span
            v-if="filterInternal.description"
            class="value"
            >: {{ filterInternal.description }}</span
          >
        </div>
        <div class="expand">
          <icon-arrow-down />
        </div>
        <div
          class="delete"
          @click.stop.prevent="remove">
          <icon-circle-x />
        </div>
      </div>
      <div
        ref="expandedPane"
        class="expanded-pane"
        :class="{ 'anchor-right': shouldAnchorRight, 'anchor-bottom': shouldAnchorBottom, visible: isExpanded }">
        <div class="inputs">
          <div class="date-options">
            <label :for="`${filterInternal.property}-withinTheLast`">
              <input
                :id="`${filterInternal.property}-withinTheLast`"
                v-model="dateFilterType"
                type="radio"
                :name="`${filterInternal.property}`"
                :value="DateFilterComparisonOptions.WithinTheLast.id"
                class="mr-2" />
              <span class="mr-2">{{ capitalize($t('messages.withinTheLast')) }}</span>
              <input
                v-model="dateFilterWithinTheLastDurationValue"
                type="text"
                class="form-control mr-2"
                style="width: 3rem"
                @click="dateFilterType = DateFilterComparisonOptions.WithinTheLast.id"
                @keyup.enter="commitDateFilterChange" />
              <select v-model="dateFilterWithinTheLastDurationUnit">
                <option value="year">{{ $t('names.year', { n: 2 }) }}</option>
                <option value="month">{{ $t('names.month', { n: 2 }) }}</option>
                <option value="day">{{ $t('names.day', { n: 2 }) }}</option>
                <option value="hour">{{ $t('names.hour', { n: 2 }) }}</option>
                <option value="minute">{{ $t('names.minute', { n: 2 }) }}</option>
                <option value="second">{{ $t('names.second', { n: 2 }) }}</option>
              </select>
            </label>
            <label :for="`${filterInternal.property}-moreThan`">
              <input
                :id="`${filterInternal.property}-moreThan`"
                v-model="dateFilterType"
                type="radio"
                :name="`${filterInternal.property}`"
                :value="DateFilterComparisonOptions.MoreThan.id"
                class="mr-2" />
              <span class="mr-2">{{ capitalize($t('messages.moreThan')) }}</span>
              <input
                v-model="dateFilterMoreThanDurationValue"
                type="text"
                class="form-control mr-2"
                style="width: 3rem"
                @click="dateFilterType = DateFilterComparisonOptions.MoreThan.id"
                @keyup.enter="commitDateFilterChange" />
              <select
                v-model="dateFilterMoreThanDurationUnit"
                class="mr-2">
                <option value="year">{{ $t('names.year', { n: 2 }) }}</option>
                <option value="month">{{ $t('names.month', { n: 2 }) }}</option>
                <option value="day">{{ $t('names.day', { n: 2 }) }}</option>
                <option value="hour">{{ $t('names.hour', { n: 2 }) }}</option>
                <option value="minute">{{ $t('names.minute', { n: 2 }) }}</option>
                <option value="second">{{ $t('names.second', { n: 2 }) }}</option>
              </select>
              <span>{{ $t('adverbs.ago') }}</span>
            </label>
            <div
              class="mr-2 date-option"
              @click="dateFilterType = DateFilterComparisonOptions.Between.id">
              <input
                :id="`${filterInternal.property}-between`"
                v-model="dateFilterType"
                type="radio"
                :name="`${filterInternal.property}`"
                :value="DateFilterComparisonOptions.Between.id"
                class="mr-2" />
              <span class="mr-2">{{ capitalize($t('messages.between')) }}</span>
              <date-picker
                style="width: 80rem"
                :use-range="true"
                input-size="sm"
                @input="dateFilterType = DateFilterComparisonOptions.Between.id"
                @keyup.enter="commitDateFilterChange"
                @range-changed="onBetweenDateRangeChanged" />
            </div>
            <div
              class="mr-2 date-option"
              @click="dateFilterType = DateFilterComparisonOptions.Before.id">
              <input
                :id="`${filterInternal.property}-before`"
                v-model="dateFilterType"
                type="radio"
                :name="`${filterInternal.property}`"
                :value="DateFilterComparisonOptions.Before.id"
                class="mr-2" />
              <span class="mr-2">{{ capitalize($t('states.before')) }}</span>
              <date-picker
                ref="beforeDatePicker"
                style="width: 30rem"
                input-size="sm"
                @input="dateFilterType = DateFilterComparisonOptions.Before.id"
                @keyup.enter="commitDateFilterChange"
                @date-changed="onBeforeDateChanged" />
            </div>
            <div
              class="mr-2 date-option"
              @click="dateFilterType = DateFilterComparisonOptions.After.id">
              <input
                :id="`${filterInternal.property}-after`"
                v-model="dateFilterType"
                type="radio"
                :name="`${filterInternal.property}`"
                :value="DateFilterComparisonOptions.After.id"
                class="mr-2" />
              <span class="mr-2">{{ capitalize($t('states.after')) }}</span>
              <date-picker
                ref="afterDatePicker"
                style="width: 30rem"
                input-size="sm"
                @input="dateFilterType = DateFilterComparisonOptions.After.id"
                @keyup.enter="commitDateFilterChange"
                @date-changed="onAfterDateChanged" />
            </div>
            <label :for="`${filterInternal.property}-inRange`">
              <input
                :id="`${filterInternal.property}-inRange`"
                v-model="dateFilterType"
                type="radio"
                :name="`${filterInternal.property}`"
                :value="DateFilterComparisonOptions.InRange.id"
                class="mr-2" />
              <span class="mr-2"
                >{{ capitalize($t('messages.inRange')) }}
                <span class="help">
                  <icon-info v-tooltip="capitalize($t('messages.dateFilterInRangeDescription'))" />
                </span>
              </span>

              <input
                v-model="dateFilterRangeStart"
                type="text"
                class="form-control mr-2"
                style="width: 8rem"
                placeholder="-3w 4d"
                @click="dateFilterType = DateFilterComparisonOptions.InRange.id"
                @keyup.enter="commitDateFilterChange" />
              <span class="mr-2">{{ $t('adverbs.to') }}</span>
              <input
                v-model="dateFilterRangeEnd"
                type="text"
                class="form-control"
                style="width: 8rem"
                placeholder="3w 4d"
                @click="dateFilterType = DateFilterComparisonOptions.InRange.id"
                @keyup.enter="commitDateFilterChange" />
            </label>
          </div>
        </div>
        <div class="actions">
          <button
            class="btn btn-sm btn-primary-hollow"
            :disabled="!isDateFilterValid"
            @click="commitDateFilterChange">
            {{ capitalize($t('actions.update')) }}
          </button>
          <button
            class="btn btn-sm btn-default"
            @click="isExpanded = !isExpanded">
            {{ capitalize($t('actions.cancel')) }}
          </button>
        </div>
      </div>
    </div>
    <!-- LIST FILTER -->
    <div v-else-if="filterInternal.type === 'ListFilter'">
      <!-- VENUE TYPE FILTER -->
      <drop-down-group
        v-if="isVenueTypeFilter"
        :allow-exclusions="
          filterInternal.allowExclusions
            ? filterInternal.options != null
              ? !filterInternal.options.some((x) => x.filters)
              : false
            : false
        "
        :is-filter="true"
        :allow-select-parent-only="true"
        :options="filterInternal.options"
        :option-label-property="filterInternal.sourceLabel"
        :option-track-by-property="filterInternal.sourceID"
        :option-label-parent-property="filterInternal.sourceLabel"
        :selected-options="filterInternal.includedAndExcludedOptions"
        :excluded-options="filterInternal.excludedOptions"
        :show-select-all="false"
        :label="filterInternal.label"
        :show-floating-label="false"
        :show-popup-header="false"
        :show-tooltip-on-button="true"
        :show-selections-in-label="true"
        :mark-as-having-selections-when-all-are-selected="true"
        :return-empty-selected-options-when-all-is-selected="false"
        :tint-when-selections="false"
        :use-table-filter-style="true"
        :show-label-before-selections="true"
        :is-disabled="isDisabled"
        :show-arrow="!isDisabled"
        @selection-changed="commitListFilterChange">
        <div
          v-if="!isDisabled"
          class="delete"
          @click.stop.prevent="remove">
          <icon-circle-x />
        </div>
      </drop-down-group>
      <drop-down
        v-else
        :allow-exclusions="allowExclusions"
        :options="filterInternal.isLoadingOptions ? [] : filteredOptions"
        :option-label-property="filterInternal.sourceLabel"
        :option-track-by-property="filterInternal.sourceID"
        :selected-options="filterInternal.includedAndExcludedOptions"
        :excluded-options="filterInternal.excludedOptions"
        :select-all-by-default="false"
        :show-select-all="false"
        :show-clear="false"
        :label="filterInternal.label"
        :show-floating-label="false"
        :show-popup-header="false"
        :show-tooltip-on-button="true"
        :show-selections-in-label="true"
        :mark-as-having-selections-when-all-are-selected="true"
        :return-empty-selected-options-when-all-is-selected="false"
        :tint-when-selections="false"
        :use-table-filter-style="true"
        :show-label-before-selections="true"
        :can-toggle-groups="filterInternal.property === 'buyer_organization_dsp_ids'"
        :use-groups="['created_by', 'buyer_organization_dsp_ids'].includes(filterInternal.property)"
        :is-disabled="isDisabled"
        :show-arrow="!isDisabled"
        @selection-changed="commitListFilterChange">
        <div
          v-if="!isDisabled"
          class="delete"
          @click.stop.prevent="remove">
          <icon-circle-x />
        </div>
      </drop-down>
    </div>
    <!-- BOOLEAN FILTER -->
    <div v-else-if="filterInternal.type === 'BooleanFilter'">
      <div
        class="filter"
        @click="
          toggleDropDown();
          booleanFilterValue = filterInternal.value;
        ">
        <div class="label">
          {{ filterInternal.label }}
          <span
            v-if="filterInternal.value != null"
            class="value"
            >: {{ filterInternal.value ? capitalize($t('actions.yes')) : capitalize($t('actions.no')) }}</span
          >
        </div>
        <div class="expand">
          <icon-arrow-down />
        </div>
        <div
          class="delete"
          @click.stop.prevent="remove">
          <icon-circle-x />
        </div>
      </div>
      <div
        ref="expandedPane"
        class="expanded-pane"
        :class="{ 'anchor-right': shouldAnchorRight, 'anchor-bottom': shouldAnchorBottom, visible: isExpanded }">
        <div class="inputs">
          <tabs-slider
            :selected-tab-i-d="booleanFilterValue === true ? 'true' : 'false'"
            :tabs="[
              { id: 'true', name: capitalize($t('actions.yes', { n: 1 })) },
              { id: 'false', name: capitalize($t('actions.no', { n: 2 })) },
            ]"
            @selected-tab-changed="(x) => (booleanFilterValue = x.id === 'true')" />
        </div>
      </div>
    </div>
    <!-- TEXT FILTER -->
    <div v-else>
      <div
        class="filter"
        @click="
          toggleTextDropDown();
          textFilterValue = filterInternal.value;
        ">
        <div class="label">
          {{ filterInternal.label
          }}<span
            v-if="filterInternal.values"
            class="value"
            >: {{ filterInternal.values.map((x) => `"${x}"`).join(', ') }}</span
          >
        </div>
        <div class="expand">
          <icon-arrow-down />
        </div>
        <div
          class="delete"
          @click.stop.prevent="remove">
          <icon-circle-x />
        </div>
      </div>
      <div
        ref="expandedPane"
        class="expanded-pane"
        :class="{ 'anchor-right': shouldAnchorRight, 'anchor-bottom': shouldAnchorBottom, visible: isExpanded }">
        <tabs-slider
          v-if="filterInternal.type !== 'UUIDFilter'"
          class="mb-3"
          width="100%"
          height="4rem"
          tab-style="font-size: 0.8em; padding: 1.2rem"
          :selected-tab-i-d="textFilterComparisonOption"
          :tabs="textFilterComparisonOptions"
          @selected-tab-changed="(x) => (textFilterComparisonOption = x.id)" />
        <div class="inputs">
          <input
            v-model="textFilterValue"
            type="text"
            class="form-control table-text-filter"
            @keyup.enter="commitTextFilterChange" />
        </div>
        <div class="actions">
          <button
            class="btn btn-sm btn-primary-hollow"
            :disabled="!isTextFilterValid"
            @click="commitTextFilterChange">
            {{ capitalize($t('actions.update')) }}
          </button>
          <button
            class="btn btn-sm btn-default"
            @click="isExpanded = !isExpanded">
            {{ capitalize($t('actions.cancel')) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { DateTime } from 'luxon';
  import { capitalize, defineAsyncComponent } from 'vue';
  import { mapGetters } from 'vuex';

  import DatePicker from '@/common/components/controls/DatePicker';
  import TabsSlider from '@/common/components/controls/TabsSlider';
  import { IconArrowDown, IconCircleX, IconInfo } from '@/common/icons/monochrome';
  import { FilterComparisonOperator } from '@/common/models/query-params';
  import DateFilter from '@/common/models/query-params/DateFilter';
  import DateFilterComparisonOptions from '@/common/models/query-params/DateFilterComparisonOptions';
  import NumericFilterComparisonOptions from '@/common/models/query-params/NumericFilterComparisonOptions';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { waitUntil } from '@/utils/DelayHelper';
  import i18n from '@/vueI18n';

  export default {
    components: {
      TabsSlider,
      DatePicker,
      DropDown: defineAsyncComponent(() => import('@/common/components/controls/dropdown/DropDown')),
      DropDownGroup: defineAsyncComponent(() => import('@/common/components/controls/dropdown/DropDownGroup')),
      IconArrowDown,
      IconCircleX,
      IconInfo,
    },
    props: {
      filter: {
        type: Object,
      },
      filterOptionsTransformation: {
        type: Function,
        required: false,
        default: null,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        booleanFilterValue: null,
        dateFilterType: null,
        filterInternal: null,
        isExpanded: false,
        selectedOptions: [],
        textFilterValue: null,
        textFilterComparisonOption: FilterComparisonOperator.LIKE,
        listSearchText: '',
        numericFilterComparisonOption: NumericFilterComparisonOptions.Equals,
        numericFilterFirstValue: '',
        numericFilterSecondValue: '',
        dateFilterWithinTheLastDurationValue: null,
        dateFilterWithinTheLastDurationUnit: 'day',
        dateFilterMoreThanDurationValue: null,
        dateFilterMoreThanDurationUnit: 'day',
        dateFilterRangeStart: null,
        dateFilterRangeEnd: null,
        dateFilterBetweenStart: null,
        dateFilterBetweenEnd: null,
        dateFilterBefore: null,
        dateFilterAfter: null,
        shouldAnchorRight: false,
        shouldAnchorBottom: false,
        wasCommitted: false,
        FilterComparisonOperator,
        DateFilterComparisonOptions,
        NumericFilterComparisonOptions,
      };
    },
    computed: {
      ...mapGetters(['user']),
      sortedOptions() {
        return [...this.filterInternal.options].sort((a, b) =>
          a[this.filterInternal.sourceLabel]
            .toLowerCase()
            .localeCompare(b[this.filterInternal.sourceLabel].toLowerCase()),
        );
      },
      filteredOptions() {
        const filterOptions =
          this.listSearchText != null
            ? this.sortedOptions.filter((x) =>
                x[this.filterInternal.sourceLabel].toLowerCase().includes(this.listSearchText.trim().toLowerCase()),
              )
            : this.sortedOptions;
        const currentUser = filterOptions.find((user) => user.userID === this.user?.id);

        if (this.filterInternal.property === 'created_by' && currentUser) {
          return [
            { name: i18n.global.t('names.currentUser', { n: 1 }), children: [currentUser] },
            {
              name: i18n.global.t('names.otherUser', filterOptions.length),
              children: filterOptions.filter((f) => f.userID !== this.user.id),
            },
          ];
        } else if (this.filterOptionsTransformation) {
          return this.filterOptionsTransformation(filterOptions);
        } else {
          return filterOptions;
        }
      },
      isFirstValueValid() {
        return !Number.isNaN(Number.parseFloat(this.numericFilterFirstValue));
      },
      isSecondValueValid() {
        return !Number.isNaN(Number.parseFloat(this.numericFilterSecondValue));
      },
      isDateFilterValid() {
        if (this.dateFilterType == null) return false;
        switch (this.dateFilterType) {
          case DateFilterComparisonOptions.WithinTheLast.id:
            return (
              this.dateFilterWithinTheLastDurationUnit != null &&
              !Number.isNaN(Number.parseFloat(this.dateFilterWithinTheLastDurationValue))
            );
          case DateFilterComparisonOptions.MoreThan.id:
            return (
              this.dateFilterMoreThanDurationUnit != null &&
              !Number.isNaN(Number.parseFloat(this.dateFilterMoreThanDurationValue))
            );
          case DateFilterComparisonOptions.Between.id:
            return (
              this.dateFilterBetweenStart != null &&
              this.dateFilterBetweenEnd != null &&
              this.dateFilterBetweenStart < this.dateFilterBetweenEnd
            );
          case DateFilterComparisonOptions.Before.id:
            return this.$refs.beforeDatePicker.$data.internalDateValue != null;
          case DateFilterComparisonOptions.After.id:
            return this.$refs.afterDatePicker.$data.internalDateValue != null;
          case DateFilterComparisonOptions.InRange.id:
            return (
              this.dateFilterRangeStart != null &&
              this.dateFilterRangeEnd != null &&
              this.dateFilterRangeStart.match(/(-?\d{1,}[a-z]{1,})/gm) != null &&
              this.dateFilterRangeEnd.match(/(-?\d{1,}[a-z]{1,})/gm) != null &&
              this.dateFilterRangeStart.match(/(-?\d{1,}[a-z]{1,})/gm).length > 0 &&
              this.dateFilterRangeEnd.match(/(-?\d{1,}[a-z]{1,})/gm).length > 0 &&
              DateFilter.getRangeDate(this.dateFilterRangeStart) < DateFilter.getRangeDate(this.dateFilterRangeEnd)
            );
          default:
            return false;
        }
      },
      isTextFilterValid() {
        const instanceClass = this.filter.constructor;
        return instanceClass.isValid == null || instanceClass.isValid(this.textFilterValue);
      },
      textFilterComparisonOptions() {
        return [
          {
            id: FilterComparisonOperator.LIKE,
            name: capitalize(i18n.global.t('messages.includes')),
          },
          {
            id: FilterComparisonOperator.EQUAL,
            name: capitalize(i18n.global.t('messages.exactMatch')),
          },
          {
            id: FilterComparisonOperator.STARTS_WITH,
            name: capitalize(i18n.global.t('messages.startsWith')),
          },
          {
            id: FilterComparisonOperator.IN,
            name: this.capitalize(i18n.global.t('messages.anyOf')),
          },
        ];
      },
      isValid() {
        if (this.numericFilterComparisonOption.id === NumericFilterComparisonOptions.IsEmpty.id) return true;

        const invalid =
          !this.isFirstValueValid ||
          (this.numericFilterComparisonOption?.id === NumericFilterComparisonOptions.Between.id &&
            !this.isSecondValueValid);
        return !invalid;
      },
      availableNumericalFilterComparisonOptions() {
        return NumericFilterComparisonOptions.all.filter((f) => !this.filter.filtersToExclude.includes(f.id));
      },
      isVenueTypeFilter() {
        return /^media_?type[._]id$/.test(this.filterInternal.property);
      },
      allowExclusions() {
        return !this.filterInternal.isLoadingOptions && this.filterInternal.allowExclusions
          ? this.filterInternal.options != null && !this.filterInternal.options.some((x) => x.filters)
          : false;
      },
    },
    watch: {
      'filter.valuesChangedExternallyAt': function () {
        this.readFilterFromProp();
      },
      filter() {
        this.readFilterFromProp();
      },
      isExpanded(val) {
        if (val) {
          if (this.filterInternal.type === 'NumericFilter') {
            this.numericFilterFirstValue = this.filterInternal.firstValue;
            this.numericFilterSecondValue = this.filterInternal.secondValue;
          } else if (this.filterInternal.type === 'BooleanFilter') {
            this.booleanFilterValue = this.filterInternal.value;
          } else {
            this.textFilterValue = (this.filterInternal.values || []).join(',');
          }
          setTimeout(() => {
            const pane = document.querySelector('.expanded-pane.visible');
            const input = pane.querySelector('input');
            if (input) {
              input.focus();
              // input.select();
            }
          }, 100);
        }
      },
      booleanFilterValue(val) {
        if (this.filterInternal.value !== val) {
          this.filterInternal.value = val;
          this.fireFiltersChanged();
        }
      },
      dateFilterType(val) {
        switch (val) {
          case DateFilterComparisonOptions.After.id:
            this.dateFilterAfter = DateTime.local().startOf('day');
            break;
          case DateFilterComparisonOptions.Before.id:
            this.dateFilterBefore = DateTime.local().startOf('day');
            break;
          default:
            break;
        }
      },
    },
    async beforeMount() {
      await this.readFilterFromProp();
    },
    async mounted() {
      window.addEventListener('resize', this.onWindowResize);
      window.addEventListener('wheel', this.onWindowMouseWheel);
    },
    beforeUnmount() {
      window.removeEventListener('wheel', this.onWindowMouseWheel);
      window.removeEventListener('resize', this.onWindowResize);
    },
    methods: {
      capitalize,
      fireFiltersChanged() {
        this.$emit('filterChanged', this.filterInternal);
      },
      remove() {
        GlobalEventBus.$emit(GlobalEvents.clearTooltips);
        this.$emit('removed', this.filterInternal);
      },
      commitTextFilterChange() {
        this.filterInternal.values =
          this.textFilterValue != null ? this.textFilterValue.split(',').map((x) => x.trim()) : [];
        this.filterInternal.comparisonOperator = this.textFilterComparisonOption;
        this.fireFiltersChanged();
        this.isExpanded = !this.isExpanded;
        this.wasCommitted = true;
      },
      commitListFilterChange(e) {
        // this.filterInternal.values = this.selectedOptions.map(x => x[this.filterInternal.sourceID]);
        this.filterInternal.values = e.included.map((x) => x[this.filterInternal.sourceID]);
        this.filterInternal.excludedValues = e.excluded.map((x) => x[this.filterInternal.sourceID]);
        this.fireFiltersChanged();
        this.wasCommitted = true;
      },
      commitNumericFilterChanges() {
        if (
          this.numericFilterComparisonOption.id !== NumericFilterComparisonOptions.IsEmpty.id &&
          (!this.isFirstValueValid ||
            (this.numericFilterComparisonOption.id === NumericFilterComparisonOptions.Between.id &&
              !this.isSecondValueValid))
        ) {
          return;
        }
        this.filterInternal.comparisonOption = this.numericFilterComparisonOption;
        this.filterInternal.firstValue = this.numericFilterFirstValue;
        this.filterInternal.secondValue = this.numericFilterSecondValue;
        this.fireFiltersChanged();
        this.isExpanded = !this.isExpanded;
        this.wasCommitted = true;
      },
      onNumericFilterComparisonOptionChanged(e) {
        const id = e.target.value;
        this.numericFilterComparisonOption = NumericFilterComparisonOptions.all.find((x) => x.id === id);
        if (this.numericFilterComparisonOption.id === NumericFilterComparisonOptions.IsEmpty.id) {
          this.numericFilterFirstValue = null;
          this.numericFilterSecondValue = null;
        }
      },
      isOptionSelected(option) {
        return this.selectedOptions.some((x) => x === option);
      },
      toggleOption(option, commitChanges = true) {
        if (this.isOptionSelected(option)) {
          this.selectedOptions = this.selectedOptions.filter((x) => x !== option);
        } else {
          this.selectedOptions.push(option);
        }
        if (commitChanges) {
          this.commitListFilterChange();
        }
      },
      onNumericFilterFirstValueChange() {
        if (!this.isFirstValueValid) {
          return;
        }
        const cleaned = `${Number.parseFloat(this.numericFilterFirstValue)}`;
        if (cleaned.localeCompare(this.numericFilterFirstValue) !== 0) {
          this.numericFilterFirstValue = cleaned;
        }
      },
      onNumericFilterSecondValueChange() {
        if (!this.isSecondValueValid) {
          return;
        }
        const cleaned = `${Number.parseFloat(this.numericFilterSecondValue)}`;
        if (cleaned.localeCompare(this.numericFilterSecondValue) !== 0) {
          this.numericFilterSecondValue = cleaned;
        }
      },
      commitDateFilterChange() {
        if (!this.isDateFilterValid) return;

        this.filterInternal.clear();
        this.filterInternal.comparisonOption = this.dateFilterType;
        switch (this.dateFilterType) {
          case DateFilterComparisonOptions.WithinTheLast.id:
            this.filterInternal.durationValue = this.dateFilterWithinTheLastDurationValue;
            this.filterInternal.durationUnit = this.dateFilterWithinTheLastDurationUnit;
            break;
          case DateFilterComparisonOptions.MoreThan.id:
            this.filterInternal.durationValue = this.dateFilterMoreThanDurationValue;
            this.filterInternal.durationUnit = this.dateFilterMoreThanDurationUnit;
            break;
          case DateFilterComparisonOptions.Between.id:
            this.filterInternal.firstDate = this.dateFilterBetweenStart;
            this.filterInternal.secondDate = this.dateFilterBetweenEnd;
            break;
          case DateFilterComparisonOptions.Before.id:
            this.filterInternal.before = this.dateFilterBefore;
            break;
          case DateFilterComparisonOptions.After.id:
            this.filterInternal.after = this.dateFilterAfter;
            break;
          case DateFilterComparisonOptions.InRange.id:
            this.filterInternal.rangeStart = this.dateFilterRangeStart;
            this.filterInternal.rangeEnd = this.dateFilterRangeEnd;
            break;
          default:
            break;
        }
        this.fireFiltersChanged();
        this.isExpanded = false;
        this.wasCommitted = true;
      },
      onBetweenDateRangeChanged(val) {
        this.dateFilterBetweenStart = val.start;
        this.dateFilterBetweenEnd = val.end;
      },
      onBeforeDateChanged(val) {
        this.dateFilterBefore = val;
      },
      onAfterDateChanged(val) {
        this.dateFilterAfter = val;
      },
      toggleNumericDropDown() {
        if (!this.isExpanded) {
          this.wasCommitted = false;
          // The following will remove any of the excluded filters from a default value. IE, if equal is excluded then it wouldn't default to it like before.
          const defaultFilterComparisonOption = this.filterInternal.filtersToExclude.length
            ? NumericFilterComparisonOptions.all.filter((o) => !this.filterInternal.filtersToExclude.includes(o.id))[0]
            : NumericFilterComparisonOptions.Equals;
          this.numericFilterComparisonOption = this.filterInternal.comparisonOption || defaultFilterComparisonOption;
          this.numericFilterFirstValue = this.filterInternal.firstValue;
          this.numericFilterSecondValue = this.filterInternal.secondValue;
        }
        this.toggleDropDown();
      },
      toggleDateDropDown() {
        if (!this.isExpanded) {
          this.dateFilterType = this.filterInternal.comparisonOption;
          switch (this.dateFilterType) {
            case DateFilterComparisonOptions.WithinTheLast.id:
              this.dateFilterWithinTheLastDurationValue = this.filterInternal.durationValue;
              this.dateFilterWithinTheLastDurationUnit = this.filterInternal.durationUnit;
              break;
            case DateFilterComparisonOptions.MoreThan.id:
              this.dateFilterMoreThanDurationValue = this.filterInternal.durationValue;
              this.dateFilterMoreThanDurationUnit = this.filterInternal.durationUnit;
              break;
            case DateFilterComparisonOptions.Between.id:
              this.dateFilterBetweenStart = this.filterInternal.firstDate;
              this.dateFilterBetweenEnd = this.filterInternal.secondDate;
              break;
            case DateFilterComparisonOptions.Before.id:
              this.dateFilterBefore = this.filterInternal.before;
              break;
            case DateFilterComparisonOptions.After.id:
              this.dateFilterAfter = this.filterInternal.after;
              break;
            case DateFilterComparisonOptions.InRange.id:
              this.dateFilterRangeStart = this.filterInternal.rangeStart;
              this.dateFilterRangeEnd = this.filterInternal.rangeEnd;
              break;
            default:
              break;
          }
        }
        this.toggleDropDown();
      },
      toggleTextDropDown() {
        if (!this.isExpanded) {
          this.textFilterComparisonOption = this.filterInternal.comparisonOperator;
        }
        this.toggleDropDown();
      },
      toggleDropDown() {
        this.isExpanded = !this.isExpanded;
        this.updateDropDownAlignment();
      },
      updateDropDownAlignment() {
        if (this.$refs.expandedPane == null) return;
        const dropDownBounding = this.$refs.expandedPane.getBoundingClientRect();
        const containerBounding = window.document.body.getBoundingClientRect();

        this.shouldAnchorRight = dropDownBounding.right > containerBounding.right;
        this.shouldAnchorBottom = dropDownBounding.bottom > containerBounding.bottom;
      },
      onWindowResize() {
        this.updateDropDownAlignment();
      },
      onWindowMouseWheel(event) {
        const getScrollParent = (node) => {
          if (node == null) {
            return null;
          }

          if (node.scrollHeight > node.clientHeight) {
            return node;
          }
          return getScrollParent(node.parentNode);
        };
        const parent = getScrollParent(this.$refs.filterBg);

        const targetContainer = this.$refs.filterBg;
        if (targetContainer != null && (event.target === targetContainer || targetContainer.contains(event.target))) {
          parent.scrollTop += event.deltaY;
        }
        if (targetContainer != null && (event.target === targetContainer || targetContainer.contains(event.target))) {
          parent.scrollTop += event.deltaY;
        }
      },
      onFilterConditionSelectionChanged(condition, selection) {
        condition.values = selection.map((x) => x[condition.parent.sourceID]);
      },
      async readFilterFromProp() {
        const assignFilter = async () => {
          const filter = this.filter.constructor.fromApi(this.filter.toApi());
          if (this.filter.type === 'ListFilter') {
            await waitUntil(() => filter.isLoadingOptions === false);
          }
          this.filterInternal = filter;
          this.filterInternal.instance = this.filter.instance;
        };
        await assignFilter();
      },
    },
  };
</script>

<style scoped lang="scss">
  @use 'sass:color';
  @use '@/styles/variables.scss' as *;

  #app {
    .table-filter-bg {
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      z-index: 1;
      background: #000;
      opacity: 0.01 !important;
    }
  }

  .table-filter {
    .delete {
      display: flex;
      align-items: center;

      svg {
        fill: #000;
        stroke: #000;
        opacity: 0.2;
        width: 1.6rem !important;
        height: 1.6rem !important;
      }
    }
  }

  .filter {
    background-color: #00000003;
    border-radius: 2rem;
    border: dashed 0.1rem $borders;
    display: flex;
    align-items: center;
    padding: 0.6rem 1.6rem;
    cursor: pointer;
    position: relative;
    box-shadow: $small-shadow;

    color: var(--defaultButtonColor);

    &:hover {
      background: var(--defaultButtonColor);
      color: var(--defaultButtonColorReverse);

      svg {
        stroke: var(--defaultButtonColorReverse);
        fill: var(--defaultButtonColorReverse);
      }
    }

    .expand {
      display: flex;
      align-items: center;

      svg {
        width: 1rem;
        height: 1rem;
      }
    }

    .label {
      font-weight: 700;
      display: flex;

      .value {
        font-weight: 400;
        max-width: 50rem;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        display: inline-block;
      }
    }

    > * {
      margin-right: 0.6rem;

      &:last-child {
        margin-right: 0;
      }
    }

    > input,
    > select {
      height: 2.8rem;
    }

    &.numeric {
      input {
        width: 9rem;
      }
    }
  }

  .expanded-pane {
    transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
    position: absolute;
    border: solid 0.1rem $borders;
    border-radius: 0.8rem;
    background: white;
    box-shadow: $big-shadow;
    z-index: 2;
    margin-top: 0.5rem;
    margin-right: 0;
    width: 40rem;
    transform-origin: top;
    transform: scaleY(0);
    opacity: 0;
    padding: 1.6rem;
    max-height: 37rem;
    display: flex;
    flex-direction: column;

    &.visible {
      transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
      transform: scaleY(1);
      opacity: 1;
    }

    &.anchor-right {
      right: 0;
    }

    &.anchor-bottom {
      //bottom: 0;
    }

    &.no-padding {
      padding: 0;
    }

    .actions {
      margin-top: 1rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .inputs {
      display: flex;
      align-items: flex-start;
      justify-content: stretch;

      select {
        height: 3.4rem;
      }

      > * {
        margin-right: 0.6rem;

        &:last-child {
          margin-right: 0;
        }
      }
    }

    ul {
      padding: 0;
      max-height: 50rem;
      overflow-y: auto;

      li {
        list-style: none;

        label {
          .checkbox-container {
            flex-shrink: 0;
          }
        }
      }
    }

    .date-options {
      font-size: 0.9em;
      display: flex;
      flex-direction: column;
      width: 100%;

      label,
      .date-option {
        display: flex;
        align-items: center;
        margin-bottom: 0.6rem;

        &:last-child {
          margin-bottom: 0;
        }

        span:first-of-type {
          flex-grow: 1;
          flex-basis: 100%;
        }

        input,
        .date-time-picker {
          flex-shrink: 0;
        }
      }
    }

    .condition {
      display: flex;
      flex-direction: column;
      font-size: 0.9em;
      border-bottom: solid 0.1rem $borders;
      background-color: #fff;
      padding: 0.8rem 1.6rem;

      .filler {
        flex-basis: 100%;
      }

      .and {
        text-transform: uppercase;
      }

      .badge {
        cursor: pointer;

        &:hover {
          background-color: var(--info);
          color: var(--infoReverse);

          svg {
            fill: var(--infoReverse);
            stroke: var(--infoReverse);
          }
        }
      }

      &:nth-of-type(even) {
        background-color: $almost-white;
      }

      &:last-of-type {
        border-bottom-width: 0;
      }
    }

    .help {
      svg {
        height: 1.2rem;
        width: 1.2rem;

        fill: var(--info);
        stroke: var(--info);
      }
    }
  }
</style>
